import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, PieChart, Download } from "lucide-react";
import { API, authHeaders, money } from "./shared";

const PRESETS = [["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["year", "This Year"], ["custom", "Custom"]];
const GROUPS = [["", "No breakdown"], ["vehicle", "By Vehicle"], ["customer", "By Customer"], ["booking", "By Booking"], ["method", "By Payment Method"]];

const Stat = ({ label, value, tone = "", testid }) => (
  <div data-testid={testid} className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3">
    <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
    <p className={`font-display font-bold text-lg tabular-nums mt-0.5 ${tone}`}>{value}</p>
  </div>
);

export default function RevenueReportModule({ token, onAuthError }) {
  const [preset, setPreset] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [groupBy, setGroupBy] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/finance/revenue-report`, { ...authHeaders(token), params: { preset, start, end, group_by: groupBy } })
      .then((r) => setD(r.data))
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (preset !== "custom" || start) load(); /* eslint-disable-next-line */ }, [preset, start, end, groupBy]);

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/finance/revenue-report/export`,
        { ...authHeaders(token), params: { preset, start, end, group_by: groupBy }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `revenue_report_${preset}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { /* noop */ }
  };

  return (
    <div className="space-y-4" data-testid="revenue-report-module">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 text-sm font-bold text-[#F2B705]"><PieChart size={15} /> Revenue Report — where every dollar came from</span>
        <button data-testid="rr-export" onClick={exportCsv}
          className="ml-auto flex items-center gap-1.5 text-xs font-semibold border border-white/20 text-white/70 px-3 py-1.5 rounded hover:border-[#F2B705] hover:text-[#F2B705] transition-colors">
          <Download size={13} /> Export CSV
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map(([k, l]) => (
          <button key={k} data-testid={`rr-preset-${k}`} onClick={() => setPreset(k)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${preset === k ? "bg-[#F2B705] text-black border-[#F2B705]" : "border-white/15 text-white/60 hover:text-white"}`}>{l}</button>
        ))}
        {preset === "custom" && (
          <span className="flex items-center gap-1.5">
            <input type="date" data-testid="rr-start" className="bg-black border border-white/20 rounded px-2 py-1 text-white text-xs" value={start} onChange={(e) => setStart(e.target.value)} />
            <span className="text-white/40 text-xs">to</span>
            <input type="date" data-testid="rr-end" className="bg-black border border-white/20 rounded px-2 py-1 text-white text-xs" value={end} onChange={(e) => setEnd(e.target.value)} />
          </span>
        )}
        <select data-testid="rr-group" className="ml-auto bg-black border border-white/20 rounded px-2 py-1.5 text-white text-xs" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
          {GROUPS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#F2B705]" size={22} /></div> : d && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Stat testid="rr-gross" label="Gross Collected" value={money(d.gross_collected)} tone="text-[#F2B705]" />
            <Stat testid="rr-autohire-revenue" label="AutoHire Revenue (excl. insurance)" value={money(d.autohire_revenue)} tone="text-emerald-300" />
            <Stat testid="rr-protection" label="All-In Coverage Collected (third-party, not AutoHire revenue)" value={money(d.protection_collected)} tone="text-sky-300" />
            <Stat testid="rr-unallocated" label="Unallocated (legacy All-In, no reliable breakdown)" value={money(d.unallocated_legacy)} tone="text-amber-300" />
            <Stat testid="rr-base" label="Base Rental Revenue" value={money(d.base_rental)} />
            <Stat testid="rr-protection-cost" label={`Modeled Provider Cost (${money(d.protection_cost_day)}/day rate — informational only)`} value={money(d.protection_cost)} tone="text-orange-300" />
            <Stat testid="rr-protection-profit" label="Modeled Provider Margin (informational only)" value={money(d.protection_profit)} tone="text-emerald-300" />
            <Stat testid="rr-insurance-settled" label="Insurance Funds Settled to Provider" value={money(d.insurance_settled_to_provider)} tone="text-sky-300" />
            <Stat testid="rr-waiver" label="Damage Waiver Revenue" value={money(d.damage_waiver)} />
            <Stat testid="rr-driver" label="Driver Fees" value={money(d.driver_fees)} />
            <Stat testid="rr-other" label="Other Fees / Charges" value={money(d.other_fees)} />
            <Stat testid="rr-legacy" label="Legacy / Unclassified" value={money(d.unclassified_legacy)} tone="text-white/50" />
            <Stat testid="rr-refunds" label="Refunds / Credits" value={money(d.refunds_credits)} tone="text-red-300" />
            <Stat testid="rr-expenses" label="Operating Expenses" value={money(d.expenses)} tone="text-red-300" />
            <Stat testid="rr-net" label="Net Profit" value={money(d.net_profit)} tone={d.net_profit >= 0 ? "text-emerald-300" : "text-red-400"} />
          </div>
          <p className="text-white/35 text-[11px]">
            All-In third-party coverage is collected from renters but is never AutoHire rental earnings — it's excluded
            from AutoHire Revenue and Net Profit here. Net Profit = AutoHire Revenue − Operating Expenses (insurance
            settlement to the provider is shown separately and never double-subtracted). Payments recorded before itemized
            allocation are shown as Legacy / Unclassified; All-In bookings with no reliable rate of their own are
            Unallocated — neither is ever guessed using today's rate.
          </p>
          {groupBy && d.groups?.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full text-xs" data-testid="rr-group-table">
                <thead><tr className="text-white/40 uppercase text-[10px] tracking-wide bg-[#0A0A0A]">
                  <th className="text-left px-3 py-2">{GROUPS.find(([k]) => k === groupBy)?.[1]}</th>
                  <th className="text-right px-3 py-2">Gross</th><th className="text-right px-3 py-2">Base</th>
                  <th className="text-right px-3 py-2">All-In Coverage</th><th className="text-right px-3 py-2">Waiver</th>
                  <th className="text-right px-3 py-2">Driver</th><th className="text-right px-3 py-2">Other</th>
                  <th className="text-right px-3 py-2">Legacy</th><th className="text-right px-3 py-2">Unallocated</th>
                </tr></thead>
                <tbody>
                  {d.groups.map((g) => (
                    <tr key={g.key} className="border-t border-white/5 text-white/75">
                      <td className="px-3 py-2 font-semibold text-white/90">{g.label}</td>
                      <td className="text-right px-3 py-2 text-[#F2B705] font-semibold">{money(g.gross)}</td>
                      <td className="text-right px-3 py-2">{money(g.base)}</td>
                      <td className="text-right px-3 py-2 text-sky-300">{money(g.protection)}</td>
                      <td className="text-right px-3 py-2">{money(g.waiver)}</td>
                      <td className="text-right px-3 py-2">{money(g.driver_fees)}</td>
                      <td className="text-right px-3 py-2">{money(g.other)}</td>
                      <td className="text-right px-3 py-2 text-white/45">{money(g.unclassified)}</td>
                      <td className="text-right px-3 py-2 text-amber-300">{money(g.unallocated)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
